import type { EmployerHistoryRow, WorkNumberEligibility, WorkNumberHistory } from "./worknumber.types";

// ---------------------------------------------------------------------------
// worknumber.mapping
//
// Pure mappers: the generated client returns `object` for every Work Number
// endpoint, so these translate the raw payloads into the app's view models. No
// React. Unit-testable and side-effect-free. Every field is read defensively —
// unknown/missing keys degrade to `false` (eligibility) or an em-dash cell
// (history) rather than throwing, so BFF field-name drift never crashes the UI.
// ---------------------------------------------------------------------------

const EMPTY_CELL = "—";

/**
 * Narrows an unknown payload to a string-keyed record, or `{}` when it is not a
 * plain object (null, array, primitive).
 * @param {unknown} value - The raw payload.
 * @returns {Record<string, unknown>} The record, or an empty object.
 */
function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/**
 * Reads the first present key from a record and renders it as a display string.
 * Numbers are stringified; null/undefined/empty become the em-dash placeholder.
 * @param {Record<string, unknown>} source - The row payload.
 * @param {...string} keys - Candidate field names in priority order.
 * @returns {string} The display value, or "—".
 */
function readCell(source: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }
  return EMPTY_CELL;
}

/**
 * Maps the eligibility payload down to the single decision the UI needs. Treats
 * the applicant as eligible when any recognised "eligible" flag is truthy; the
 * untouched payload is preserved on `raw` for later flag surfacing.
 * @param {unknown} payload - The raw `/eligibility` response.
 * @returns {WorkNumberEligibility} The reduced eligibility view model.
 */
export function mapEligibility(payload: unknown): WorkNumberEligibility {
  const raw = asRecord(payload);
  const canPull = Boolean(raw.canPull ?? raw.eligible ?? raw.isEligible);
  return { canPull, raw };
}

/**
 * Maps a single raw history entry to a fully-populated table row, filling any
 * missing column with an em-dash.
 * @param {unknown} entry - One raw history record.
 * @returns {EmployerHistoryRow} The table row.
 */
function mapRow(entry: unknown): EmployerHistoryRow {
  const row = asRecord(entry);
  return {
    employerName: readCell(row, "employerName"),
    employeeStatus: readCell(row, "employeeStatus", "employmentStatus"),
    occupation: readCell(row, "occupation"),
    hireDate: readCell(row, "hireDate"),
    payFrequency: readCell(row, "payFrequency"),
    payRate: readCell(row, "payRate"),
    payPeriodFrequency: readCell(row, "payPeriodFrequency"),
  };
}

/**
 * Maps a work-number history/pull payload into table rows. Accepts either a bare
 * array of entries or an object wrapping the entries under a common key
 * (`rows` / `history` / `employments`); anything else yields no rows.
 * @param {unknown} payload - The raw `/history` or POST `/worknumber` response.
 * @returns {WorkNumberHistory} The history view model.
 */
export function mapHistory(payload: unknown): WorkNumberHistory {
  const entries = Array.isArray(payload)
    ? payload
    : (() => {
        const record = asRecord(payload);
        const candidate = record.rows ?? record.history ?? record.employments;
        return Array.isArray(candidate) ? candidate : [];
      })();
  return { rows: entries.map((entry) => mapRow(entry)) };
}
